import React, { useMemo } from 'react';

interface ShiftInfo {
  code: string;
  name: string;
  start_time: string;
  end_time: string;
}

interface SolverAssignment {
  employee_id: string;
  date: string;
  shift_id: string;
}

interface EmployeeInfo {
  name: string;
  position: string | null;
}

interface SolutionCalendarGridProps {
  periodStart: string; // YYYY-MM-DD
  periodEnd: string;
  assignments: SolverAssignment[];
  employeeInfo: Map<string, EmployeeInfo>;
  shiftByCode: Map<string, ShiftInfo>;
}

/**
 * Палитра по shift.code. Для произвольных кодов берётся хеш-цвет.
 */
const KNOWN_COLORS: Record<string, string> = {
  morning: 'bg-blue-100 text-blue-800 border-blue-300',
  day: 'bg-green-100 text-green-800 border-green-300',
  evening: 'bg-purple-100 text-purple-800 border-purple-300',
  night: 'bg-slate-200 text-slate-800 border-slate-400',
  full: 'bg-amber-100 text-amber-800 border-amber-300',
};

function colorForCode(code: string): string {
  if (KNOWN_COLORS[code]) return KNOWN_COLORS[code];
  let h = 0;
  for (let i = 0; i < code.length; i++) h = (h * 31 + code.charCodeAt(i)) | 0;
  const palette = [
    'bg-rose-100 text-rose-800 border-rose-300',
    'bg-teal-100 text-teal-800 border-teal-300',
    'bg-orange-100 text-orange-800 border-orange-300',
    'bg-cyan-100 text-cyan-800 border-cyan-300',
    'bg-lime-100 text-lime-800 border-lime-300',
  ];
  return palette[Math.abs(h) % palette.length];
}

/**
 * Русская буква смены: сначала по первой кириллической букве имени
 * («День…» → Д), затем по префиксу кода, затем по часу начала.
 */
function baseRuLetter(shift: ShiftInfo): string {
  const trimmed = shift.name.trim();
  const m = trimmed.match(/^[А-ЯЁа-яё]/);
  if (m && !/^смена/i.test(trimmed)) return m[0].toUpperCase();
  const prefixMap: Array<[RegExp, string]> = [
    [/^morning/, 'У'],
    [/^(day|noon|full)/, 'Д'],
    [/^(evening|late)/, 'В'],
    [/^night/, 'Н'],
  ];
  for (const [re, letter] of prefixMap) {
    if (re.test(shift.code)) return letter;
  }
  const hour = parseInt(shift.start_time.slice(0, 2), 10);
  if (hour >= 5 && hour < 11) return 'У';
  if (hour >= 11 && hour < 17) return 'Д';
  if (hour >= 17 && hour < 22) return 'В';
  return 'Н';
}

/**
 * Метки для всех смен: буква + номер при совпадении букв (Д1, Д2).
 * Ключ — тот же, что в shiftByCode (shift_id).
 */
function buildShiftLabels(shiftByCode: Map<string, ShiftInfo>): Map<string, string> {
  const entries = Array.from(shiftByCode.entries()).sort(
    (a, b) =>
      a[1].start_time.localeCompare(b[1].start_time) ||
      a[1].code.localeCompare(b[1].code),
  );
  const byLetter = new Map<string, Array<[string, ShiftInfo]>>();
  for (const e of entries) {
    const letter = baseRuLetter(e[1]);
    const group = byLetter.get(letter) ?? [];
    group.push(e);
    byLetter.set(letter, group);
  }
  const labels = new Map<string, string>();
  for (const [letter, group] of byLetter) {
    group.forEach(([key], i) => {
      labels.set(key, group.length > 1 ? `${letter}${i + 1}` : letter);
    });
  }
  return labels;
}

export const SolutionCalendarGrid: React.FC<SolutionCalendarGridProps> = ({
  periodStart,
  periodEnd,
  assignments,
  employeeInfo,
  shiftByCode,
}) => {
  const dates = useMemo(() => {
    const out: string[] = [];
    const start = new Date(periodStart + 'T00:00:00Z');
    const end = new Date(periodEnd + 'T00:00:00Z');
    for (let t = start.getTime(); t <= end.getTime(); t += 86_400_000) {
      out.push(new Date(t).toISOString().slice(0, 10));
    }
    return out;
  }, [periodStart, periodEnd]);

  /** {employeeId: {date: shift_id}} */
  const byEmpDate = useMemo(() => {
    const m = new Map<string, Map<string, string>>();
    for (const a of assignments) {
      const inner = m.get(a.employee_id) ?? new Map<string, string>();
      inner.set(a.date, a.shift_id);
      m.set(a.employee_id, inner);
    }
    return m;
  }, [assignments]);

  /** Сортируем сотрудников по убыванию количества назначенных дней. */
  const empIds = useMemo(() => {
    return Array.from(byEmpDate.keys()).sort((a, b) => {
      const ca = byEmpDate.get(a)?.size ?? 0;
      const cb = byEmpDate.get(b)?.size ?? 0;
      return cb - ca;
    });
  }, [byEmpDate]);

  const shiftLabels = useMemo(() => buildShiftLabels(shiftByCode), [shiftByCode]);

  if (empIds.length === 0) {
    return (
      <div className="border rounded-md p-8 text-center text-muted-foreground">
        Нет назначений.
      </div>
    );
  }

  const dayLabel = (iso: string): { day: string; dow: string } => {
    const d = new Date(iso + 'T00:00:00Z');
    const dow = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'][(d.getUTCDay() + 6) % 7];
    return { day: String(d.getUTCDate()).padStart(2, '0'), dow };
  };

  const totalDaysInPeriod = dates.length;

  return (
    <div className="border rounded-md">
      <div className="px-3 py-2 border-b bg-gray-50">
        <div className="text-sm font-medium">
          Календарь смен · {empIds.length} сотр. × {totalDaysInPeriod} дн.
        </div>
      </div>
      <div className="overflow-auto max-h-[60vh]">
        <table className="border-collapse w-full text-xs">
          <thead className="sticky top-0 bg-white z-10">
            <tr>
              <th className="sticky left-0 bg-white z-20 px-2 py-1.5 border-b border-r text-left font-medium min-w-[220px]">
                Сотрудник
              </th>
              {dates.map((iso) => {
                const { day, dow } = dayLabel(iso);
                const isWeekend = dow === 'сб' || dow === 'вс';
                return (
                  <th
                    key={iso}
                    className={`px-1 py-1 border-b border-r font-normal text-center min-w-[28px] ${
                      isWeekend ? 'bg-gray-50 text-gray-700' : ''
                    }`}
                    title={iso}
                  >
                    <div className="leading-tight">
                      <div className="font-medium">{day}</div>
                      <div className="text-[9px] text-gray-500">{dow}</div>
                    </div>
                  </th>
                );
              })}
              <th
                className="sticky right-0 bg-white z-20 px-2 py-1 border-b border-l text-center font-medium min-w-[60px]"
                title="Раб. / Вых."
              >
                <div className="leading-tight">
                  <div className="text-[10px] text-green-700">Раб.</div>
                  <div className="text-[10px] text-gray-500">Вых.</div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {empIds.map((empId) => {
              const inner = byEmpDate.get(empId);
              const workDays = inner?.size ?? 0;
              const restDays = totalDaysInPeriod - workDays;
              const info = employeeInfo.get(empId);
              const name = info?.name ?? empId.slice(0, 8);
              const position = info?.position;
              return (
                <tr key={empId} className="hover:bg-gray-50">
                  <td className="sticky left-0 bg-white z-10 px-2 py-1 border-b border-r whitespace-nowrap">
                    <div className="font-medium leading-tight">{name}</div>
                    {position && (
                      <div className="text-[10px] text-gray-500 leading-tight">
                        {position}
                      </div>
                    )}
                  </td>
                  {dates.map((iso) => {
                    const shiftId = inner?.get(iso);
                    const shift = shiftId ? shiftByCode.get(shiftId) : null;
                    const label = shiftId
                      ? shiftLabels.get(shiftId) ?? '?'
                      : '?';
                    return (
                      <td
                        key={iso}
                        className="border-b border-r p-0.5 text-center"
                      >
                        {shift ? (
                          <div
                            className={`inline-block w-full px-0.5 py-0.5 rounded text-[10px] font-bold border ${colorForCode(
                              shift.code,
                            )}`}
                            title={`${shift.name} (${shift.start_time}–${shift.end_time})`}
                          >
                            {label}
                          </div>
                        ) : (
                          <span className="text-gray-300">·</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="sticky right-0 bg-white z-10 px-2 py-1 border-b border-l text-center whitespace-nowrap">
                    <div className="text-[11px] font-semibold text-green-700 leading-tight">
                      {workDays}
                    </div>
                    <div className="text-[10px] text-gray-500 leading-tight">
                      {restDays}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-3 py-2 border-t bg-gray-50 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px]">
        <span className="font-medium text-gray-700">Обозначения:</span>
        {Array.from(shiftByCode.entries())
          .sort(
            (a, b) =>
              a[1].start_time.localeCompare(b[1].start_time) ||
              a[1].code.localeCompare(b[1].code),
          )
          .map(([id, s]) => (
            <span key={id} className="inline-flex items-center gap-1.5">
              <span
                className={`inline-flex items-center justify-center min-w-[22px] px-1 py-0.5 rounded border text-[10px] font-bold ${colorForCode(
                  s.code,
                )}`}
              >
                {shiftLabels.get(id) ?? '?'}
              </span>
              <span className="text-gray-600">
                {s.name} ({s.start_time.slice(0, 5)}–{s.end_time.slice(0, 5)})
              </span>
            </span>
          ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center min-w-[22px] px-1 py-0.5 rounded border border-gray-200 bg-white text-gray-400 text-[10px]">
            ·
          </span>
          <span className="text-gray-600">выходной</span>
        </span>
      </div>
    </div>
  );
};
